'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import type { RankStripItem } from '@/lib/product-rank-strip';
import { useRankStrip } from './product-rank-strip-context';

const STRIP_IMAGE_SIZES = '(min-width: 1024px) 150px, 28vw';
const MAX_ITEMS = 14;

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

function normalizeType(value?: string): string {
	return (value ?? '').trim().toLowerCase();
}

interface Crumb {
	name: string;
	url?: string;
}

interface ProductRankStripProps {
	/** Server-computed list for this product: current product first, then collection/recommendations. */
	seedItems: RankStripItem[];
	/** Handle of the product currently being viewed (drives the highlight). */
	currentHandle: string;
	/** Current product type, for the breadcrumb leaf. */
	productType?: string;
	/** Collection handles the current product belongs to (drives seed reuse). */
	collectionHandles: string[];
	/** Handle of the collection the server built this seed around ('' if none). */
	primaryCollection: string;
}

function hasReusableStoredSeed(
	rail: ReturnType<typeof useRankStrip>,
	productType: string | undefined,
	collectionHandles: string[],
) {
	const targetType = normalizeType(productType);
	return Boolean(
		rail &&
			rail.seedItems.length > 0 &&
			rail.seedCollection !== '' &&
			rail.seedType === targetType &&
			collectionHandles.some((handle) => handle.toLowerCase() === rail.seedCollection),
	);
}

export function ProductRankStrip({
	seedItems,
	currentHandle,
	productType,
	collectionHandles,
	primaryCollection,
}: ProductRankStripProps) {
	const rail = useRankStrip();
	const pathname = usePathname();
	const scrollRef = useRef<HTMLUListElement>(null);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);
	const [page, setPage] = useState(1);
	const [pages, setPages] = useState(1);

	const currentItem = seedItems[0];
	const scrollPositionRef = rail?.scrollPosition;

	// Reuse the stored seed while browsing the same trade/theme collection so the
	// rail stays put (only the current-product highlight moves). It's reused when
	// the current product belongs to the seed's collection and shares its type.
	const reuseStoredSeed = hasReusableStoredSeed(rail, productType, collectionHandles);

	// Seed a fresh session when we aren't reusing an existing one.
	useEffect(() => {
		if (!reuseStoredSeed && seedItems.length > 0) {
			rail?.setSeedItems(seedItems, primaryCollection, productType ?? '');
		}
	}, [reuseStoredSeed, rail, seedItems, primaryCollection, productType]);

	// The rail: the stable seed for this session, with the current product
	// guaranteed present so its highlight always renders.
	const items = useMemo(() => {
		const base = reuseStoredSeed && rail ? rail.seedItems : seedItems;
		const hasCurrent = base.some((item) => item.handle === currentHandle);
		const merged = hasCurrent || !currentItem ? base : [currentItem, ...base];
		return merged.slice(0, MAX_ITEMS);
	}, [reuseStoredSeed, rail, seedItems, currentItem, currentHandle]);

	const updateScrollState = useCallback(() => {
		const element = scrollRef.current;
		if (!element) return;
		const { scrollLeft, scrollWidth, clientWidth } = element;
		const width = Math.max(clientWidth, 1);
		setCanPrev(scrollLeft > 4);
		setCanNext(scrollLeft + clientWidth < scrollWidth - 4);
		const maxScroll = Math.max(0, scrollWidth - clientWidth);
		const totalPages = maxScroll > 4 ? Math.floor(maxScroll / width) + 1 : 1;
		setPages(totalPages);
		setPage(Math.min(totalPages, Math.floor(scrollLeft / width) + 1));
		if (scrollPositionRef) scrollPositionRef.current = scrollLeft;
	}, [scrollPositionRef]);

	// Restore the saved offset on mount so the rail keeps its position when landing
	// on a new PDP in the same session (forced instant, bypassing scroll-smooth).
	useIsomorphicLayoutEffect(() => {
		const element = scrollRef.current;
		if (!element || !scrollPositionRef) return;
		const previousBehavior = element.style.scrollBehavior;
		element.style.scrollBehavior = 'auto';
		element.scrollLeft = scrollPositionRef.current;
		element.style.scrollBehavior = previousBehavior;
	}, [scrollPositionRef]);

	useEffect(() => {
		const element = scrollRef.current;
		if (!element) return;
		updateScrollState();
		element.addEventListener('scroll', updateScrollState, { passive: true });
		window.addEventListener('resize', updateScrollState);
		return () => {
			element.removeEventListener('scroll', updateScrollState);
			window.removeEventListener('resize', updateScrollState);
		};
	}, [updateScrollState]);

	const scrollByViewport = useCallback((direction: 1 | -1) => {
		const element = scrollRef.current;
		if (!element) return;
		element.scrollBy({ left: direction * element.clientWidth * 0.8, behavior: 'smooth' });
	}, []);

	const productHref = useCallback(
		(handle: string) => {
			const normalizedPathname = pathname.replace(/\/$/, '');
			const currentProductPath = `/products/${currentHandle}`;
			if (normalizedPathname.endsWith(currentProductPath)) {
				return `${normalizedPathname.slice(0, -currentHandle.length)}${handle}`;
			}

			return `/products/${handle}`;
		},
		[pathname, currentHandle],
	);

	if (items.length === 0) return null;

	const crumbs: Crumb[] = [{ name: 'Home', url: '/' }, { name: 'Products' }];
	if (productType) crumbs.push({ name: productType });

	const showIndicator = pages > 1;
	const safePage = Math.min(page, pages);

	return (
		<div className="container-page pt-4">
			<div className="mb-3 flex min-h-5 items-center justify-between gap-4">
				<nav aria-label="Breadcrumb" className="min-w-0">
					<ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-neutral-500 text-xs uppercase tracking-wider">
						{crumbs.map((crumb, index) => {
							const isLast = index === crumbs.length - 1;
							return (
								<li
									key={`${crumb.name}-${crumb.url ?? 'leaf'}`}
									className="flex items-center gap-x-2"
								>
									{crumb.url && !isLast ? (
										<Link href={crumb.url} className="transition-colors hover:text-neutral-900">
											{crumb.name}
										</Link>
									) : (
										<span className={isLast ? 'text-neutral-900' : undefined}>{crumb.name}</span>
									)}
									{!isLast && (
										<span aria-hidden="true" className="text-neutral-300">
											/
										</span>
									)}
								</li>
							);
						})}
					</ol>
				</nav>
				{showIndicator && (
					<div className="flex shrink-0 items-center gap-2 text-neutral-500 text-sm">
						<button
							type="button"
							onClick={() => scrollByViewport(-1)}
							disabled={!canPrev}
							aria-label="Scroll to previous products"
							className="transition-colors hover:text-neutral-900 disabled:opacity-30"
						>
							<ChevronLeft className="h-4 w-4" />
						</button>
						<span className="tabular-nums">
							{safePage} / {pages}
						</span>
						<button
							type="button"
							onClick={() => scrollByViewport(1)}
							disabled={!canNext}
							aria-label="Scroll to next products"
							className="transition-colors hover:text-neutral-900 disabled:opacity-30"
						>
							<ChevronRight className="h-4 w-4" />
						</button>
					</div>
				)}
			</div>
			<ul
				ref={scrollRef}
				aria-label="Related products"
				className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
			>
				{items.map((item) => {
					const isCurrent = item.handle === currentHandle;

					return (
						<li
							key={item.handle}
							className="w-28 min-w-0 shrink-0 snap-start sm:w-32 md:w-36 lg:w-40"
						>
							<Link
								href={productHref(item.handle)}
								prefetch
								aria-current={isCurrent ? 'page' : undefined}
								className="group block"
							>
								<div
									className={`relative aspect-[3/4] overflow-hidden rounded border-2 bg-neutral-100 transition-colors ${
										isCurrent
											? 'border-neutral-900'
											: 'border-transparent group-hover:border-neutral-300'
									}`}
								>
									{item.image && (
										<ShopifyImage
											src={item.image.url}
											alt={item.image.altText || item.title}
											fill
											loading="lazy"
											className="object-cover"
											sizes={STRIP_IMAGE_SIZES}
										/>
									)}
								</div>
								<p className="mt-1.5 min-h-7 line-clamp-2 font-semibold text-[10px] text-neutral-900 uppercase leading-tight group-hover:underline sm:text-[11px]">
									{item.title}
								</p>
							</Link>
						</li>
					);
				})}
			</ul>
		</div>
	);
}
